import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDrawer } from "../DrawerContext";
import LifestylePage from "../LifestylePage";
import AllergiesPage from "./AllergiesPage";
import ConditionsPage from "./ConditionsPage";
import DocumentsPage from "./DocumentsPage";
import FindDoctorPage from "./FindDoctorPage";
import HistoryPage from "./HistoryPage";
import HospitalPage from "./HospitalPage";
import InsurancePage from "./InsurancePage";
import MedicationsPage from "./MedicationsPage";
import PatientSidebar from "./PatientSidebar";
import ProceduresPage from "./ProceduresPage";
import SalMineCardPage from "./SalMineCardPage";

const tabComponentMap = {
  Insurance: InsurancePage,
  Medications: MedicationsPage,
  Allergies: AllergiesPage,
  Conditions: ConditionsPage,
  Procedures: ProceduresPage,
  History: HistoryPage,
  Lifestyle: LifestylePage,
  Doctors: FindDoctorPage,
  Hospitals: HospitalPage,
  Documents: DocumentsPage,
  "SAL Card": SalMineCardPage,
};

export const navItems = [
  { label: "Insurance", icon: "🛡️", translationKey: "insurance" },
  { label: "Medications", icon: "💊", translationKey: "medications" },
  { label: "Allergies", icon: "🌿", translationKey: "allergies" },
  { label: "Conditions", icon: "🩺", translationKey: "conditions" },
  { label: "Procedures", icon: "🩹", translationKey: "procedures" },
  { label: "History", icon: "📜", translationKey: "history" },
  { label: "Lifestyle", icon: "🏃", translationKey: "lifestyle" },
  { label: "Doctors", icon: "👨‍⚕️", translationKey: "doctors" },
  { label: "Hospitals", icon: "🏥", translationKey: "hospitals" },
  { label: "Documents", icon: "📄", translationKey: "documents" },
  { label: "SAL Card", icon: "💳", translationKey: "salCard" },
  {label : "Settings", icon : "⚙️", translationKey : "Settings"}
];

export const PatientDashboard = ({ lang = "en" }) => {
  const tabOrder = navItems.map((item) => item.label);

  const { drawerOpen, closeDrawer } = useDrawer();

  const navigate = useNavigate()

  const [selectedTab, setSelectedTab] = useState("Insurance");

  // Save selected tab to localStorage whenever it changes
  const handleTabChange = useCallback(
    (tab) => {
      if (tab === "Settings") {
        navigate("/settings-dashboard");
        closeDrawer();
        return; // 🚀 stop further execution
      }

      setSelectedTab(tab);
      localStorage.setItem("patientDashboardSelectedTab", tab);
      closeDrawer();
    },
    [closeDrawer, navigate]
  );

  const goToTab = useCallback(
    (direction) => {
      const idx = tabOrder.indexOf(selectedTab);
      if (direction === "next" && idx < tabOrder.length - 1) {
        const nextTab = tabOrder[idx + 1];
        setSelectedTab(nextTab);
        localStorage.setItem("patientDashboardSelectedTab", nextTab);
      } else if (direction === "prev" && idx > 0) {
        const prevTab = tabOrder[idx - 1];
        setSelectedTab(prevTab);
        localStorage.setItem("patientDashboardSelectedTab", prevTab);
      }
    },
    [selectedTab],
  );

  const SelectedComponent =
    tabComponentMap[selectedTab] ||
    (() => <div className="p-8">Not Found</div>);

  // Drawer overlay for mobile
  return (
    <div className="flex min-h-screen bg-[#f8fafc] relative">
      {/* Sidebar for desktop, Drawer for mobile */}
      <PatientSidebar
        selectedTab={selectedTab}
        onSelectTab={handleTabChange}
        lang={lang}
        isDrawer={drawerOpen}
        onCloseDrawer={closeDrawer}
        items={navItems}
        showBottomContent
      />
      {/* Overlay for drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
          onClick={closeDrawer}
        />
      )}
      <div className="flex-1 bg-[#f8fafc] p-0 overflow-visible">
        <SelectedComponent
          lang={lang}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          goToTab={goToTab}
        />
      </div>
    </div>
  );
};
